import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const FacilityChart = React.memo(({ facilityId, facilityChartData, facilityColor = '#5470c6' }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    // 初始化图表
    const initChart = () => {
        if (!chartRef.current) return;
        
        // 查找对应设施的图表数据
        const facilityChart = facilityChartData.find(item => item.facilityId === facilityId);
        if (!facilityChart || !facilityChart.chartData.length) return;
        
        // 清除之前的图表实例
        if (chartInstanceRef.current) {
            echarts.dispose(chartInstanceRef.current);
        }
        
        const myChart = echarts.init(chartRef.current);
        chartInstanceRef.current = myChart;
        
        // 直接使用 overall_score，如果不存在显示 0
        const displayScore = facilityChart.overallScore || 0;
        
        const option = {
            backgroundColor: '#fff',
            title: {
                text: ` ${displayScore.toFixed(2)}`,
                left: 'center',
                top: '1%',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: '#333'
                }
            },
            grid: {
                left: '10%',
                right: '10%',
                top: '10%',
                bottom: '10%'
            },
            tooltip: {
                trigger: 'item',
                confine: false, // 允许tooltip超出canvas边界
                enterable: true, // 允许鼠标进入tooltip
                appendToBody: true, // 将tooltip添加到body，避免被容器限制
                extraCssText: 'z-index: 9999;' // 设置更高的z-index
            },
            legend: {
                show: false
            },
            radar: {
                indicator: facilityChart.chartData.map(item => ({
                    name: item.name,
                    max: 10 // 设置最大值为10
                })),
                center: ['50%', '50%'],
                radius: '40%', // 调整半径以适应6个指标
                axisName: {
                    color: '#666',
                    fontSize: 11, // 稍微减小字体以适应更多指标
                    formatter: function(value) {
                        // 优化文字换行逻辑，适应6个指标
                        if (value.length > 8) {
                            // 找到合适的断点进行换行
                            const words = value.split(' ');
                            if (words.length > 1) {
                                const midPoint = Math.ceil(words.length / 2);
                                return words.slice(0, midPoint).join(' ') + '\n' + words.slice(midPoint).join(' ');
                            } else {
                                // 单个长单词，在合适位置断开
                                const midPoint = Math.ceil(value.length / 2);
                                return value.substring(0, midPoint) + '\n' + value.substring(midPoint);
                            }
                        }
                        return value;
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                },
                axisLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                }
            },
            series: [{
                type: 'radar',
                data: [{
                    value: facilityChart.chartData.map(item => item.value), // 使用当前值
                    name: facilityChart.facilityName || 'Facility Performance',
                    itemStyle: {
                        color: facilityColor // 直接使用传入的设施颜色
                    },
                    lineStyle: {
                        color: facilityColor // 直接使用传入的设施颜色
                    },
                    areaStyle: {
                        color: facilityColor, // 直接使用传入的设施颜色
                        opacity: 0.1
                    }
                }]
            }]
        };
        
        myChart.setOption(option);
        
        // 响应式
        const resizeHandler = () => {
            myChart.resize();
        };
        window.addEventListener('resize', resizeHandler);
        
        // 清理函数
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    };

    useEffect(() => {
        const cleanup = initChart();
        return cleanup;
    }, [facilityId, facilityChartData, facilityColor]);

    useEffect(() => {
        return () => {
            if (chartInstanceRef.current) {
                echarts.dispose(chartInstanceRef.current);
            }
        };
    }, []);

    return (
        <div 
            ref={chartRef}
            style={{ width: '100%', height: '100%' }}
        />
    );
}, (prevProps, nextProps) => {
    // 自定义比较函数，只有在这些属性真正改变时才重新渲染
    return (
        prevProps.facilityId === nextProps.facilityId &&
        prevProps.facilityColor === nextProps.facilityColor &&
        prevProps.facilityChartData === nextProps.facilityChartData
    );
});

// 设置 displayName 用于调试
FacilityChart.displayName = 'FacilityChart';

export default FacilityChart;
